<template>
  <div class="analysis">
    <v-container fluid class="analysis-container analysis-container--with-side-menu">
      <!-- The top page-title strip (e.g. "Personalisierungsstudio",
           "360° Grundriss-Analyse") was removed: the side menu already
           highlights the active surface, so the h1 was redundant and
           pushed the actual content down. The content blocks each carry
           their own contextual heading. -->

      <div class="analysis-shell">
        <!-- Fallback rail on the upload state. When there is no
             analysis loaded yet the full AnalysisSideMenu can't
             render (no product mode), but the user still needs an
             entry-point to their account + Meine Analysen on desktop. -->
        <AppAccountRail
          v-if="!analysis && !$vuetify.display.smAndDown"
          standalone
          class="analysis-shell-side"
        />
        <AnalysisSideMenu
          v-if="analysis && productMode !== null && !$vuetify.display.smAndDown"
          class="analysis-shell-side"
          :analysis-id="analysisRouteId"
          :mode="productMode"
          :report-tabs="tabs"
          :active-tab="activeTab"
          :ordered-nodes="orderedStudioNodes"
          :current-node-id="studioTree.currentNodeId"
          :tree-busy="studioTree.busy"
          :can-export-branch="studioCanExportBranch"
          :export-busy="studioExportBusy"
          :is-uploading="isUploading"
          :show360-pdf-export="Boolean(analysis && showExportControl && productMode === '360')"
          :can-export-pdf="canExportPdf"
          :has-premium-access="hasPremiumAccess"
          :export-pdf-loading="showExportLoading"
          @navigate-tab="activeTab = $event"
          @select-node="handleStudioSelectNode"
          @studio-export-branch="handleStudioExportBranch"
          @upload-new="showUploadArea = !showUploadArea"
          @export-pdf-360="handleExportPdf"
        />

      <div class="analysis-shell-main">

      <v-slide-y-transition>
        <div v-if="showUploadArea">
          <div v-if="!analysis" class="analysis-upload-intro">
            <p class="analysis-upload-eyebrow">
              {{ isCreditedUploader ? t('analysis.uploadIntro.eyebrowPaid') : t('analysis.uploadIntro.eyebrow') }}
            </p>
            <h1>{{ t('analysis.uploadIntro.title') }}</h1>
            <p>{{ isCreditedUploader ? t('analysis.uploadIntro.descriptionPaid') : t('analysis.uploadIntro.description') }}</p>
          </div>
          <v-row>
            <v-col cols="12">
              <UploadArea 
                @upload-success="handleUploadSuccess" 
                @upload-state-change="handleUploadStateChange"
              />
            </v-col>
          </v-row>
        </div>
      </v-slide-y-transition>

      <template v-if="analysis && !isUploading">
        <div v-if="showTrialPreviewBanner" class="trial-preview-region">
          <v-alert
            type="info"
            variant="tonal"
            class="mb-4 trial-preview-banner"
            data-testid="trial-preview-banner"
          >
            <div class="font-weight-bold mb-1">
              {{ t('analysis.trialPreview.title') }}
            </div>
            <p class="mb-0 text-body-2">
              {{ t('analysis.trialPreview.description') }}
            </p>
          </v-alert>
        </div>

        <AnalysisProductHub
          v-if="productMode === 'hub'"
          :hub="productHub"
          :image-url="imageUrl"
          :analysis-id="analysisRouteId"
          :loading-state="sectionStatus"
          @open-360="trackProductEvent('hub_cta_360_clicked')"
          @open-studio="trackProductEvent('hub_cta_studio_clicked')"
        />

        <StudioDashboard
          v-else-if="productMode === 'studio'"
          :hub="productHub"
          :suggestions="studioSuggestions"
          :image-url="imageUrl"
          :analysis-id="analysisRouteId"
          :has-premium-access="hasPremiumAccess"
          :user-intent="store.state.userIntent"
          :starter-pack-derivation="studioStarterPackDerivation"
          :starter-pack-loading="studioStarterPackLoading"
          :starter-pack-error="studioStarterPackError"
          @retry-starter-pack="retryStudioStarterPack"
          :nodes="studioTree.nodes"
          :current-node-id="studioTree.currentNodeId"
          :pending-generation="studioTree.pending"
          :tree-busy="studioTree.busy"
          :drift-hint-visible="studioDriftHintVisible"
          :stub-mode="studioTree.stubMode"
          :initial-free-text="studioPrefillText"
          @unlock="handleStudioUnlock"
          @suggestion-seen="handleStudioSuggestionSeen"
          @generate="handleStudioGenerate"
          @select-node="handleStudioSelectNode"
          @dismiss-drift="handleStudioDismissDrift"
          @cancel-generation="handleStudioCancelGeneration"
          @export-branch="handleStudioExportBranch"
          :export-busy="studioExportBusy"
          :menu-driven="!$vuetify.display.smAndDown"
        />

        <div v-else-if="productMode === '360'" class="analysis-report-shell">
          <!-- AnalysisSectionNav is kept for mobile only — on desktop the
               360 sub-tabs live inside the unified AnalysisSideMenu, so
               rendering this card alongside duplicates navigation.
               $vuetify.display.smAndDown is true at < 960 px which matches
               the side menu's mobile-drawer breakpoint. -->
          <AnalysisSectionNav
            v-if="$vuetify.display.smAndDown"
            v-model="activeTab"
            :tabs="tabs"
            :show-loading-status="showSectionLoadingStatus"
            :loading-section-labels="loadingSectionLabels"
          />

          <div class="analysis-report-content">
            <v-window v-model="activeTab" class="analysis-report-window">
              <v-window-item :value="0">
                <v-row>
                  <v-col cols="12">
                    <AnalysisResults :results="results" />
                  </v-col>
                </v-row>
              </v-window-item>

              <v-window-item :value="1">
                <v-row>
                  <v-col cols="12">
                    <RoomEvaluation :imageUrl="imageUrl" />
                  </v-col>
                </v-row>
              </v-window-item>

              <v-window-item :value="2">
                <v-row>
                  <v-col cols="12">
                    <CostEfficiencyAnalysis :imageUrl="imageUrl" />
                  </v-col>
                </v-row>
              </v-window-item>

              <v-window-item :value="3">
                <v-row>
                  <v-col cols="12">
                    <ImprovementSuggestions
                      ref="improvementSuggestionsRef"
                      :original-image-url="imageUrl"
                      :teaser-status="teaserStatus"
                      :teaser-image-src="teaserImageSrc"
                    />
                  </v-col>
                </v-row>
              </v-window-item>
            </v-window>
          </div>
        </div>
      </template>

      <v-row
        v-if="analysis && !hasPremiumAccess && productMode !== 'studio'"
        class="mt-6"
      >
        <v-col cols="12">
          <UnlockReportPanel
            :user-intent="store.state.userIntent"
            @unlock="handleUnlockCta"
          />
        </v-col>
      </v-row>

      <v-row v-if="!analysis" class="mt-6">
        <v-col cols="12">
          <PremiumFeatures
            variant="secondary"
            tracking-source="analysis_premium_features"
          />
        </v-col>
      </v-row>

      </div>
      </div>

    </v-container>

    <PayPalButton
      v-if="analysis"
      v-model="showAnalysisPayPal"
      source="analysis_panel"
      context="analysis_unlock"
      :user-intent="store.state.userIntent"
      @payment-success="handleAnalysisPaymentSuccess"
      @payment-error="handleAnalysisPaymentError"
    />

    <ContactButton v-if="analysis && (!$vuetify.display.smAndDown || (productMode === '360' && activeTab === 0))" />

    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="4000"
      :location="$vuetify.display.smAndDown ? 'bottom' : 'top'"
      class="text-center"
    >
      {{ snackbar.text }}
    </v-snackbar>

    <!-- Saved-analyses dialog was replaced by the dedicated history
         dashboard at /meine-analysen — same data, richer view, and one
         less surface to maintain. -->
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { useDisplay } from 'vuetify'
import {
  trackAnalysisLoaded,
  trackAnalysisTabView,
  trackAuthGateOpened,
  trackPersonalizedTeaserFailed,
  trackPersonalizedTeaserImpression,
  trackPersonalizedTeaserLoadingSeen,
  trackPersonalizedTeaserReady,
  trackPersonalizedTeaserRequested,
  trackPdfExport,
  trackEvent,
  trackUnlockCtaClicked,
} from '@/utils/analytics'
import { useRoute, useRouter } from 'vue-router'
import AnalysisResults from '@/components/AnalysisResults.vue'
import ImprovementSuggestions from '@/components/ImprovementSuggestions.vue'
import PremiumFeatures from '@/components/PremiumFeatures.vue'
import CostEfficiencyAnalysis from '@/components/CostEfficiencyAnalysis.vue'
import RoomEvaluation from '@/components/RoomEvaluation.vue'
import AnalysisSectionNav from '@/components/analysis/AnalysisSectionNav.vue'
import AnalysisProductHub from '@/components/analysis/AnalysisProductHub.vue'
import AnalysisSideMenu from '@/components/analysis/AnalysisSideMenu.vue'
import AppAccountRail from '@/components/AppAccountRail.vue'
import StudioDashboard from '@/components/analysis/StudioDashboard.vue'
import UnlockReportPanel from '@/components/analysis/UnlockReportPanel.vue'
import PayPalButton from '@/components/common/PayPalButton.vue'
import { generatePdf } from '@/utils/pdfmakeExporter'
import { exportStudioBranchPdf } from '@/utils/studioBranchExporter'
import { useAuthStore } from '@/stores/auth'
import ContactButton from '@/components/common/ContactButton.vue'
import axios from 'axios'
import { getIdToken } from 'firebase/auth'
import UploadArea from '@/components/upload/UploadArea.vue'
import { scrollToUnlockPanel } from '@/utils/scrollToUnlockPanel'
import { buildUnlockCtaPayload } from '@/utils/unlockTracking.mjs'
import {
  canStartNewPremiumAnalysis,
  canViewCurrentAnalysisPremium,
} from '@/utils/currentAnalysisAccess.mjs'
import { getAnalysisReadiness } from '@/utils/uploadState.mjs'
import {
  buildAnalysisUnlockQuery,
  clearAnalysisUnlockQuery,
  readAnalysisUnlockIntent,
} from '@/utils/unlockFlow.mjs'
import { canRequestPersonalizedTeaser } from '@/utils/personalizedTeaserEligibility.mjs'
import {
  TRIAL_PREVIEW_BANNER_HIDE_DELAY_MS,
  hasSeenTrialPreviewBanner,
  markTrialPreviewBannerSeen,
} from '@/utils/trialPreviewBanner.mjs'
import {
  buildHubViewModel,
  buildStudioSuggestions,
  buildSuggestionsFromStarterPack,
  getAnalysisProductMode,
} from '@/utils/productExperience.mjs'
import { loadPricingRules, getFallbackRules } from '@/utils/pricingRulesCache.mjs'
import {
  captureStudioHandoff,
  readStudioHandoff,
  clearStudioHandoff,
  hasStudioHandoff,
} from '@/utils/studioHandoff.mjs'
import { readStudioPrefill } from '@/utils/studioPrefillStash.mjs'
import {
  saveTrialSnapshot,
  readTrialSnapshot,
  clearTrialSnapshot,
} from '@/utils/trialSnapshot.mjs'
import { buildRequirementsCacheKey, buildSectionCacheKey } from '@/store'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, locale } = useI18n()
const authStore = useAuthStore()
const display = useDisplay()

const snackbar = ref({
  show: false,
  text: '',
  color: 'success'
})

const showNotification = (text, color = 'success') => {
  snackbar.value = {
    show: true,
    text,
    color
  }
}

// Computed properties for data from Vuex
const analysis = computed(() => store.state.analysis.analysis)
const results = computed(() => {
  // Get the categories from the analysis state
  const categories = [
    'raumaufteilung',
    'raumfluss',
    'lichteinfall',
    'funktionalitaet',
    'stauraum',
    'privatsphaere',
    'zugaenglichkeit',
    'flexibilitaet',
    'aussenbezug'
  ]
  
  // Transform the data into the format expected by AnalysisPdfLayout
  return categories
    .filter(cat => analysis.value?.[cat])
    .map(cat => {
      const category = analysis.value[cat]
      const aspects = {}
      
      Object.entries(category).forEach(([key, value]) => {
        if (key !== 'description' && typeof value === 'object') {
          aspects[key] = {
            ...value,
            criteria: value.description?.split('\n\n')[1] || ''
          }
        }
      })
      
      return {
        name: cat,
        description: category.description,
        aspects
      }
    })
})
const suggestions = computed(() => analysis.value?.suggestions || [])
const imageUrl = computed(() => store.state.analysis.imageUrl)
const costAnalysis = computed(() => store.state.analysisCost)
const roomAnalysis = computed(() => store.state.analysisRooms)
const productMode = computed(() => getAnalysisProductMode(route))
const analysisRouteId = computed(() => String(route.params.id || store.state.analysis.analysisId || 'current'))
const pricingRules = ref(getFallbackRules('paid'))
const productHub = computed(() =>
  buildHubViewModel({
    analysis: analysis.value,
    costAnalysis: costAnalysis.value,
    roomAnalysis: roomAnalysis.value,
    evaluation: store.state.evaluation,
    userIntent: store.state.userIntent,
    credits: authStore.credits,
    pricingRules: pricingRules.value,
    t,
  }),
)
// Two-path suggestion source:
// 1. User briefing exists → the backend-tailored starter pack is the
//    canonical source (its descriptions ARE the downstream image-gen
//    prompts; the 360 analysis's suggestions are not). While that AI
//    call is still in flight (or has errored), return [] so the
//    dashboard renders a skeleton / retry UI instead of stacking
//    look-alike heuristic cards under the loading spinner.
// 2. User skipped the briefing → no starter pack will ever be
//    generated, so we fall back to the local heuristic so Studio
//    is still useful (generic archetypes seeded from analysis.suggestions).
const studioSuggestions = computed(() => {
  const starterPack = store.state.studioStarterPack
  const pack = Array.isArray(starterPack?.suggestions) ? starterPack.suggestions : null
  if (pack && pack.length) {
    return buildSuggestionsFromStarterPack(pack, t)
  }
  const status = store.state.analysisSectionStatus?.studioStarterPack
  if (status === 'loading' || status === 'error') {
    return []
  }
  return buildStudioSuggestions({
    analysis: analysis.value,
    userIntent: store.state.userIntent,
    t,
  })
})

const studioStarterPackDerivation = computed(() => {
  const derivation = store.state.studioStarterPack?.derivation
  return typeof derivation === 'string' ? derivation : ''
})

const studioStarterPackLoading = computed(
  () => store.state.analysisSectionStatus.studioStarterPack === 'loading',
)
const studioStarterPackError = computed(
  () => store.state.analysisSectionStatus.studioStarterPack === 'error',
)

async function retryStudioStarterPack() {
  if (!analysis.value || !imageUrl.value) return
  const authHeader = await buildAuthHeader()
  await store.dispatch('preloadStudioStarterPack', {
    analysisId: store.state.analysis.analysisId || null,
    imageUrl: imageUrl.value,
    userIntent: store.state.userIntent,
    language: locale.value,
    authHeader,
  })
}
// While a product-mode route is loading (or the saved doc lacks the analysis
// blob) the body falls back to the upload zone. Suppress the product-specific
// header copy in that window so the title doesn't promise a 2-product hub or a
// Studio command bar that the body isn't actually showing.
// The previous `pageTitle` + `isAwaitingAnalysisForRoute` computeds
// existed only to drive the topbar h1 ("Personalisierungsstudio" / "360°
// Grundriss-Analyse"). The topbar is gone — the side menu is the source
// of truth for which surface is active. Both computeds are dropped.
// pageSubtitle was rendered in the old big-header layout. The unified side
// menu makes navigation explicit, so the topbar is now title-only and the
// subtitle is intentionally dropped. Keeping a no-op reference here only
// if some downstream test or component import still expects it would be
// dead code; if you re-add a subtitle UI later, recompute on-demand.

// Add a computed property to check if all data is loaded
const canExportPdf = computed(() => {
  return !!(
    analysis.value && // Main analysis data
    store.state.analysisCost && // Cost analysis
    store.state.analysisRooms && // Room analysis
    imageUrl.value // Image URL
  )
})

// Check if user has premium access
const hasPremiumAccess = computed(() =>
  canViewCurrentAnalysisPremium({
    userHadPremiumWhenUploaded: store.state.userHadPremiumWhenUploaded,
  }),
)
const hasCreditsForNewPremiumAnalysis = computed(() =>
  canStartNewPremiumAnalysis({ credits: authStore.credits }),
)
// A signed-in user who already holds credits gets a full paid analysis, not a
// free trial — so the "kostenloser Check / mehr Tiefe später freischalten"
// intro copy is wrong for them. Switch to credit-aware copy in that case.
const isCreditedUploader = computed(
  () => authStore.isAuthenticated && Number(authStore.credits) > 0,
)
const trialPreviewBannerVisible = ref(false)
let trialPreviewBannerTimer = null
let reportShellAlignmentTimer = null
let reportShellAlignmentFrame = null
const REPORT_SHELL_TOP_OFFSET = 96
const showTrialPreviewBanner = computed(() =>
  Boolean(analysis.value) &&
  !hasPremiumAccess.value &&
  trialPreviewBannerVisible.value,
)
const sectionStatus = computed(() => store.state.analysisSectionStatus || {
  rooms: 'idle',
  costs: 'idle',
  requirements: 'idle',
})
const analysisReadiness = computed(() => {
  const readiness = getAnalysisReadiness({
    analysis: analysis.value,
    roomAnalysis: roomAnalysis.value,
    costAnalysis: costAnalysis.value,
    evaluation: store.state.evaluation,
    userIntent: store.state.userIntent,
  })

  if (!readiness.baseReportReady) {
    return readiness
  }

  return {
    ...readiness,
    rooms: sectionStatus.value.rooms,
    costs: sectionStatus.value.costs,
    requirements: sectionStatus.value.requirements,
  }
})
const loadingSectionLabels = computed(() => {
  const labels = []

  if (analysisReadiness.value.rooms === 'loading') {
    labels.push(t('analysis.tabs.rooms'))
  }
  if (analysisReadiness.value.costs === 'loading') {
    labels.push(t('analysis.tabs.costs'))
  }

  return labels.join(', ')
})
const showSectionLoadingStatus = computed(() =>
  Boolean(analysis.value) &&
  analysisReadiness.value.baseReportReady &&
  Boolean(loadingSectionLabels.value),
)
const showExportControl = computed(() =>
  Boolean(analysis.value) &&
  productMode.value === '360' &&
  (
    !display.smAndDown.value ||
    (canExportPdf.value && hasPremiumAccess.value)
  ),
)
const showExportLoading = computed(() =>
  Boolean(analysis.value) &&
  hasPremiumAccess.value &&
  !canExportPdf.value &&
  !display.smAndDown.value,
)
const hasUserExpectations = computed(() =>
  Boolean(store.state.userIntent?.expectations?.trim()),
)
const canGeneratePersonalizedTeaser = computed(() =>
  canRequestPersonalizedTeaser({
    analysis: analysis.value,
    imageUrl: imageUrl.value,
    hasPremiumAccess: hasPremiumAccess.value,
    userIntent: store.state.userIntent,
  }),
)
// Tooltip text for the export button was attached to the header export
// button; the unified side menu doesn't surface a tooltip there. If we
// want hover hints back in the menu later, recompute on-demand inside
// the side-menu component (it already receives canExportPdf +
// hasPremiumAccess + exportPdfLoading as props).

// Saved-analyses list moved to the dedicated /meine-analysen
// dashboard view. The dialog + local fetch+cache that used to live
// here are gone — the dashboard fetches its own copy on mount.

const showAnalysisPayPal = ref(false)
const teaserStatus = ref('idle')
const teaserImageSrc = ref('')
const teaserRequested = ref(false)
const teaserImpressionFired = ref(false)
const improvementSuggestionsRef = ref(null)
let teaserSectionObserver = null

const openUploadAreaForPremiumRerun = async () => {
  showUploadArea.value = true
  await nextTick()
  document.querySelector('.upload-card')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  })
}

const buildAuthHeader = async () => {
  if (!authStore.isAuthenticated) {
    return undefined
  }

  const token = await getIdToken(authStore.user)
  return `Bearer ${token}`
}

const disconnectTeaserSectionObserver = () => {
  if (teaserSectionObserver) {
    teaserSectionObserver.disconnect()
    teaserSectionObserver = null
  }
}

const clearTrialPreviewBannerTimer = () => {
  if (trialPreviewBannerTimer) {
    clearTimeout(trialPreviewBannerTimer)
    trialPreviewBannerTimer = null
  }
}

const clearReportShellAlignmentWork = () => {
  if (reportShellAlignmentTimer) {
    clearTimeout(reportShellAlignmentTimer)
    reportShellAlignmentTimer = null
  }

  if (reportShellAlignmentFrame) {
    window.cancelAnimationFrame(reportShellAlignmentFrame)
    reportShellAlignmentFrame = null
  }
}

const alignReportShellAfterSectionChange = () => {
  const shell = document.querySelector('.analysis-report-shell')
  if (!shell) return

  const rect = shell.getBoundingClientRect()
  const isAboveHeaderBand = rect.top < REPORT_SHELL_TOP_OFFSET
  const isTooFarBelowViewport = rect.top > window.innerHeight * 0.45

  if (!isAboveHeaderBand && !isTooFarBelowViewport) return

  window.scrollTo({
    top: window.scrollY + rect.top - REPORT_SHELL_TOP_OFFSET,
    behavior: 'auto',
  })
}

const scheduleReportShellAlignment = () => {
  if (display.smAndDown.value) return

  clearReportShellAlignmentWork()
  alignReportShellAfterSectionChange()

  reportShellAlignmentFrame = window.requestAnimationFrame(() => {
    reportShellAlignmentFrame = null
    alignReportShellAfterSectionChange()
  })

  reportShellAlignmentTimer = setTimeout(() => {
    reportShellAlignmentTimer = null
    alignReportShellAfterSectionChange()
  }, 360)
}

const hideTrialPreviewBanner = () => {
  trialPreviewBannerVisible.value = false
  clearTrialPreviewBannerTimer()
  nextTick(() => {
    scheduleReportShellAlignment()
  })
}

const maybeShowTrialPreviewBanner = () => {
  if (
    !analysis.value ||
    hasPremiumAccess.value ||
    hasSeenTrialPreviewBanner()
  ) {
    hideTrialPreviewBanner()
    return
  }

  markTrialPreviewBannerSeen()
  trialPreviewBannerVisible.value = true
  clearTrialPreviewBannerTimer()
  trialPreviewBannerTimer = setTimeout(() => {
    hideTrialPreviewBanner()
  }, TRIAL_PREVIEW_BANNER_HIDE_DELAY_MS)
}

const resetPersonalizedTeaserState = () => {
  disconnectTeaserSectionObserver()
  teaserStatus.value = 'idle'
  teaserImageSrc.value = ''
  teaserRequested.value = false
  teaserImpressionFired.value = false
}

const observeImprovementsTeaserSection = async () => {
  if (!hasPremiumAccess.value) return
  await nextTick()
  const sectionEl = improvementSuggestionsRef.value?.teaserSectionRef
  if (!sectionEl || teaserSectionObserver) return

  teaserSectionObserver = new IntersectionObserver((entries) => {
    if (entries.some((entry) => entry.isIntersecting)) {
      if (!teaserImpressionFired.value) {
        teaserImpressionFired.value = true
        trackPersonalizedTeaserImpression({ placement: 'improvements_tab_premium' })
      }
      if (
        canGeneratePersonalizedTeaser.value &&
        teaserStatus.value === 'idle'
      ) {
        void requestPersonalizedTeaser()
      }
    }
  }, { threshold: 0.3 })

  teaserSectionObserver.observe(sectionEl)
}

const requestPersonalizedTeaser = async () => {
  if (
    teaserRequested.value ||
    !canGeneratePersonalizedTeaser.value
  ) {
    return
  }

  const userEmail = authStore.user?.email
  if (!userEmail) {
    return // backend requires userEmail; nothing to do for unauth users
  }

  teaserRequested.value = true
  teaserStatus.value = 'loading'

  const basePayload = {
    purpose: store.state.userIntent?.purpose,
    locale: locale.value,
    hasExpectations: Boolean(store.state.userIntent?.expectations?.trim()),
  }

  trackPersonalizedTeaserRequested({
    purpose: store.state.userIntent?.purpose,
    locale: locale.value,
  })
  trackPersonalizedTeaserLoadingSeen({
    purpose: store.state.userIntent?.purpose,
    locale: locale.value,
    requirementsStatus: sectionStatus.value.requirements,
  })

  try {
    // The teaser endpoint is auth-required; userEmail is derived from the
    // Firebase token server-side (the body field is ignored). Keeping it
    // in the body for a release cycle so a stale client still works, but
    // do NOT rely on it for ownership.
    const token = await getIdToken(authStore.user)
    const response = await axios.post('/api/personalized-teaser', {
      userIntent: store.state.userIntent || {},
      evaluation: store.state.evaluation || '',
      analysis: analysis.value,
      imageUrl: imageUrl.value,
      language: locale.value,
    }, {
      headers: { Authorization: `Bearer ${token}` },
    })

    const cachedFlag = Boolean(response.data?.data?.cached)
    teaserImageSrc.value = response.data?.data?.imageUrl || ''
    teaserStatus.value = teaserImageSrc.value ? 'ready' : 'error'

    if (teaserStatus.value === 'ready') {
      trackPersonalizedTeaserReady({
        ...basePayload,
        cached: cachedFlag,
      })
    } else {
      trackPersonalizedTeaserFailed({
        purpose: store.state.userIntent?.purpose,
        locale: locale.value,
        reason: 'empty_payload',
      })
    }
  } catch (error) {
    console.error('Failed to request personalized teaser:', error)
    teaserStatus.value = 'error'
    trackPersonalizedTeaserFailed({
      purpose: store.state.userIntent?.purpose,
      locale: locale.value,
      reason: 'request_failed',
    })
  }
}

const preloadSupportingAnalyses = async ({ analysisData, targetImageUrl }) => {
  // `suppressAutoSave` is intentionally NOT checked here. That flag
  // exists to keep the debounced auto-save watchers quiet while we
  // hydrate Vuex from a saved analysis — it has no business gating
  // fetches. Previously this guard plus the matching one in onMounted
  // caused the studio starter-pack call to be swallowed during
  // hydration: the optimistic 'loading' status got set, the dispatch
  // was suppressed, the 500ms timeout flipped the flag back, but no
  // event re-fired the preload — so the spinner stayed forever.
  if (!analysisData || !targetImageUrl) {
    return
  }

  const authHeader = await buildAuthHeader()

  await Promise.allSettled([
    store.dispatch('preloadRoomAnalysis', {
      imageUrl: targetImageUrl,
      language: locale.value,
      authHeader,
    }),
    store.dispatch('preloadCostAnalysis', {
      imageUrl: targetImageUrl,
      language: locale.value,
      authHeader,
    }),
    store.dispatch('preloadUserEvaluation', {
      analysis: analysisData,
      imageUrl: targetImageUrl,
      userIntent: store.state.userIntent,
      language: locale.value,
      authHeader,
    }),
    store.dispatch('preloadStudioStarterPack', {
      analysisId: store.state.analysis.analysisId || null,
      imageUrl: targetImageUrl,
      userIntent: store.state.userIntent,
      language: locale.value,
      authHeader,
    }),
  ])
}

const hydrateLoadedAnalysisSectionCache = (data) => {
  const imageUrlForCache = data?.imageUrl || null
  const keys = {}

  if (data?.roomAnalysis) {
    keys.rooms = buildSectionCacheKey({
      imageUrl: imageUrlForCache,
      language: locale.value,
      modelQuality: store.state.modelQuality,
    })
  }

  if (data?.costAnalysis) {
    keys.costs = buildSectionCacheKey({
      imageUrl: imageUrlForCache,
      language: locale.value,
      modelQuality: store.state.modelQuality,
    })
  }

  if (data?.evaluation) {
    keys.requirements = buildRequirementsCacheKey({
      imageUrl: imageUrlForCache,
      language: locale.value,
      userIntent: data?.userIntent,
    })
  }

  if (data?.studioStarterPack) {
    keys.studioStarterPack = buildRequirementsCacheKey({
      imageUrl: imageUrlForCache,
      language: locale.value,
      userIntent: data?.userIntent,
    })
  }

  store.commit('setAnalysisSectionCacheKeys', {
    rooms: keys.rooms || null,
    costs: keys.costs || null,
    requirements: keys.requirements || null,
    studioStarterPack: keys.studioStarterPack || null,
  })
}

// ── Anonymous trial persistence ────────────────────────────────────────
// An anonymous trial result lives only in Vuex, so a refresh / deep-link /
// hard navigation previously bounced the user to a blank upload page. Mirror
// the trial into sessionStorage (per tab) and rehydrate on mount. Authenticated
// analyses are unaffected — they persist to Firestore and load by id.
const persistTrialSnapshot = () => {
  if (authStore.isAuthenticated) return
  if (!analysis.value || !imageUrl.value) return
  // A real (owned) analysis id means this is no longer a throwaway trial.
  if (store.state.analysis.analysisId) return
  saveTrialSnapshot({
    analysis: analysis.value,
    imageUrl: imageUrl.value,
    analysisRooms: roomAnalysis.value || null,
    analysisCost: costAnalysis.value || null,
    studioStarterPack: store.state.studioStarterPack || null,
    userIntent: store.state.userIntent || null,
    evaluation: store.state.evaluation || null,
    sectionStatus: store.state.analysisSectionStatus,
    sectionCacheKey: store.state.analysisSectionCacheKey,
    modelQuality: store.state.modelQuality,
  })
}

const hydrateFromTrialSnapshot = (snap) => {
  store.commit('setAnalysis', { analysis: snap.analysis, imageUrl: snap.imageUrl })
  if (snap.analysisRooms) store.commit('setAnalysisRooms', snap.analysisRooms)
  if (snap.analysisCost) store.commit('setAnalysisCost', snap.analysisCost)
  store.commit('setStudioStarterPack', snap.studioStarterPack || null)
  store.commit('setUserIntent', snap.userIntent || null)
  if (snap.evaluation) store.commit('setEvaluation', snap.evaluation)
  if (snap.sectionCacheKey) store.commit('setAnalysisSectionCacheKeys', snap.sectionCacheKey)
  if (snap.sectionStatus) {
    Object.entries(snap.sectionStatus).forEach(([section, status]) => {
      store.commit('setAnalysisSectionStatus', { section, status })
    })
  }
  store.commit('setLastAnalyzedImage', snap.imageUrl)
  showUploadArea.value = false
}

// Modify handleAnalysisSelect to close the dialog
const handleAnalysisSelect = async (analysisId) => {
  if (!analysisId) return;
  const wasBareAnalysisRoute = route.name === 'Analysis'
  try {
    const token = await getIdToken(authStore.user);
    const response = await axios.get(`/api/analysis/${analysisId}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (response.data.success) {
      const data = response.data.data;
      // Suppress auto-save while loading
      suppressAutoSave.value = true;
      // Update Vuex store with the loaded data
      store.commit('setAnalysis', {
        analysisId,
        analysis: data.analysis,
        imageUrl: data.imageUrl,
        personalizedTeaserImageUrl: data.personalizedTeaserImageUrl ?? null,
      });
      store.commit('setAnalysisCost', data.costAnalysis);
      store.commit('setAnalysisRooms', data.roomAnalysis);
      store.commit('setUserIntent', data.userIntent);
      store.commit('setEvaluation', data.evaluation);
      if (data.studioStarterPack) {
        store.commit('setStudioStarterPack', {
          derivation: data.studioStarterPack.derivation,
          suggestions: data.studioStarterPack.suggestions,
        });
      } else {
        store.commit('setStudioStarterPack', null);
      }
      hydrateLoadedAnalysisSectionCache(data);
      store.commit('setAnalysisSectionStatus', {
        section: 'rooms',
        status: data.roomAnalysis ? 'ready' : 'idle',
      });
      store.commit('setAnalysisSectionStatus', {
        section: 'costs',
        status: data.costAnalysis ? 'ready' : 'idle',
      });
      store.commit('setAnalysisSectionStatus', {
        section: 'requirements',
        status: data.evaluation ? 'ready' : (data.userIntent?.expectations?.trim() ? 'loading' : 'idle'),
      });
      store.commit('setAnalysisSectionStatus', {
        section: 'studioStarterPack',
        status: data.studioStarterPack ? 'ready' : (data.userIntent?.expectations?.trim() ? 'loading' : 'idle'),
      });
      // Update lastAnalyzedImage when loading saved analysis
      store.commit('setLastAnalyzedImage', data.imageUrl);
      // allow vieweing premium content since saving was only possible with sufficient credits
      store.commit('setUserHadPremiumWhenUploaded', true);
      // hide upload area
      showUploadArea.value = false;
      if (wasBareAnalysisRoute) {
        await router.replace({
          name: 'AnalysisHub',
          params: { id: analysisId },
        });
      }
      // Show success notification
      showNotification(t('analysis.savedAnalyses.loadSuccess'), 'success');
      // Re-enable auto-save after a tick (let watchers run and settle)
      setTimeout(() => { suppressAutoSave.value = false; }, 500);
    }
  } catch (error) {
    console.error('Failed to load analysis:', error);
    showNotification(t('analysis.savedAnalyses.loadError'), 'error');
  }
}

// Add this watch effect after the other watch effects
watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated, wasAuthenticated) => {
    // Only run when changing from unauthenticated to authenticated
    if (isAuthenticated && !wasAuthenticated) {
      // The anonymous trial snapshot is no longer relevant once signed in —
      // the authenticated Firestore-backed flow owns persistence now.
      clearTrialSnapshot()
      // Deep-link hydration race fix: when a user opens
      // /analysis/:id or /analysis/:id/studio while still unauth'd
      // (e.g. the auth observer hadn't fired yet, or they signed in
      // in another tab), the initial onMounted hydration was skipped.
      // If we now have auth + a real id + no loaded analysis, hydrate
      // before the redirect-to-upload heuristic can fire and bounce
      // them to /analysis. Without this, deep links flap to the empty
      // upload surface even after sign-in.
      const routeId = route.params?.id
      if (routeId && routeId !== 'current' && !analysis.value) {
        try {
          await handleAnalysisSelect(routeId)
        } catch (err) {
          console.warn('Post-auth hydration of deep link failed:', err?.message)
        }
      }
    }
  }
)

// When the user switches language mid-session, re-fetch supporting
// analyses so the rooms / costs / Studio requirements context reflect the new
// locale instead of the stale cached one. The store-level cache key
// includes locale, so this dispatch is a no-op if the cache already
// holds the right language (e.g. on initial mount).
watch(
  () => locale.value,
  async () => {
    if (!analysis.value || !imageUrl.value || suppressAutoSave.value) return
    await preloadSupportingAnalyses({
      analysisData: analysis.value,
      targetImageUrl: imageUrl.value,
    })
  }
)

const needsAnalysisFallback = () =>
  productMode.value !== null &&
  !analysis.value &&
  (
    !route.params.id ||
    route.params.id === 'current' ||
    !authStore.isAuthenticated
  )

const redirectToUploadIfNoAnalysis = async () => {
  if (!needsAnalysisFallback()) return false
  showUploadArea.value = true
  await router.replace({ name: 'Analysis' })
  return true
}

const refreshPricingRules = async ({ force = false } = {}) => {
  try {
    const tier = authStore.isAuthenticated ? 'paid' : 'trial'
    // Credit costs are keyed by tier only — no locale dimension.
    pricingRules.value = await loadPricingRules(tier, { force })
  } catch (error) {
    console.warn('Pricing rules load failed:', error?.message)
  }
}

// Modify the onMounted hook
onMounted(async () => {
  // Scroll to top
  window.scrollTo(0, 0)
  void refreshPricingRules()

  // Wait for Firebase auth state to settle so a deep-linked /analysis/:id
  // doesn't get bounced to /analysis just because the auth observer hadn't
  // fired yet at mount time.
  if (authStore.loading) {
    await new Promise((resolve) => {
      const stop = watch(
        () => authStore.loading,
        (loading) => {
          if (!loading) {
            stop()
            resolve()
          }
        },
        { immediate: true },
      )
    })
  }

  if (analysisRouteId.value && analysisRouteId.value !== 'current') {
    void refreshStudioTree(analysisRouteId.value)
  }

  if (
    productMode.value !== null &&
    route.params.id &&
    route.params.id !== 'current' &&
    authStore.isAuthenticated
  ) {
    await handleAnalysisSelect(route.params.id)
  }

  // Anonymous trial rehydration: not signed in, on a product route, nothing
  // loaded — but a trial snapshot exists for this tab. Restore it so a refresh
  // or deep-link doesn't bounce to the empty upload page.
  if (
    productMode.value !== null &&
    !analysis.value &&
    !authStore.isAuthenticated
  ) {
    const snap = readTrialSnapshot()
    if (snap) {
      hydrateFromTrialSnapshot(snap)
    }
  }

  if (await redirectToUploadIfNoAnalysis()) return

  // if we got here from main page (without uploading an image), show the upload area as default
  if (!analysis.value) {
    showUploadArea.value = true
  }
  
  resumeAnalysisUnlockFromQuery()

  // Fire the preload directly — the suppressAutoSave guard previously
  // here raced with handleAnalysisSelect's 500ms suppress window and
  // ate the call. The watch on (analysis, imageUrl, userIntent) below
  // keeps its own suppressAutoSave check because it would otherwise
  // double-fire during the same hydration burst.
  if (analysis.value && imageUrl.value) {
    preloadSupportingAnalyses({
      analysisData: analysis.value,
      targetImageUrl: imageUrl.value,
    })
  }

  void observeImprovementsTeaserSection()
})

onBeforeUnmount(() => {
  disconnectTeaserSectionObserver()
  clearTrialPreviewBannerTimer()
  clearReportShellAlignmentWork()
})

const handleExportPdf = async () => {
  if (!hasPremiumAccess.value) {
    return
  }

  // Track the export attempt
  trackPdfExport()
  trackProductEvent('export_pdf_started', {
    surface: productMode.value || 'analysis',
  })

  const analysisData = {
    imageUrl: imageUrl.value,
    floorPlan: analysis.value,
    costAnalysis: costAnalysis.value,
    roomAnalysis: roomAnalysis.value,
    suggestions: suggestions.value,
  }

  try {
    await generatePdf(analysisData, t)
    trackProductEvent('export_pdf_succeeded', {
      surface: productMode.value || 'analysis',
    })
  } catch (error) {
    console.error('Failed to export PDF:', error)
    showNotification(t('analysis.export.failed'), 'error')
    trackProductEvent('export_pdf_failed', {
      surface: productMode.value || 'analysis',
      reason: error?.message || 'unknown',
    })
  }
}

import debounce from 'lodash/debounce'

// Flag to suppress auto-save when loading a saved analysis
const suppressAutoSave = ref(false);

// Debounced save function to avoid excessive requests
const debouncedSaveAnalysis = debounce(async () => {
  if (suppressAutoSave.value) return;
  // if no relevant property is set (e.g. if state has been reset), skip storing
  if (!analysis.value && !costAnalysis.value && !roomAnalysis.value) return;
  if (!authStore.isAuthenticated || !hasPremiumAccess.value) return;
  try {
    const token = await getIdToken(authStore.user)
    // Only send fields whose value is currently set. The backend merges
    // partial updates by treating `undefined` as "leave alone" and any
    // other value (including null) as "overwrite". A naive POST that
    // includes still-pending fields as null would clobber previously-saved
    // room/cost analyses while the second AI call is still in flight.
    const payload = { imageUrl: imageUrl.value }
    if (analysis.value) payload.analysis = analysis.value
    if (costAnalysis.value) payload.costAnalysis = costAnalysis.value
    if (roomAnalysis.value) payload.roomAnalysis = roomAnalysis.value
    if (store.state.userIntent) payload.userIntent = store.state.userIntent
    if (store.state.evaluation) payload.evaluation = store.state.evaluation
    if (store.state.studioStarterPack) payload.studioStarterPack = store.state.studioStarterPack
    const response = await axios.post('/api/store-analysis', payload, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (response.data?.analysisId) {
      store.commit('setAnalysisId', response.data.analysisId)
      await canonicalizeCurrentAnalysisRoute(response.data.analysisId)
    }
    showNotification(t('analysis.saveAnalysis.autoSaveSuccess'), 'success');
    // The saved-analyses list now lives on the /meine-analysen
    // dashboard, which fetches its own copy on mount — no in-view
    // cache to refresh here.
  } catch (error) {
    console.error('Failed to auto-save analysis:', error);
    showNotification(t('analysis.saveAnalysis.error'), 'error');
  }
}, 1000); // 1 second debounce

// Watchers for partial data changes
watch(analysis, () => { if (!suppressAutoSave.value) debouncedSaveAnalysis(); }, { deep: true });
watch(costAnalysis, () => { if (!suppressAutoSave.value) debouncedSaveAnalysis(); }, { deep: true });
watch(roomAnalysis, () => { if (!suppressAutoSave.value) debouncedSaveAnalysis(); }, { deep: true });
watch(() => store.state.studioStarterPack, () => { if (!suppressAutoSave.value) debouncedSaveAnalysis(); }, { deep: true });
watch(imageUrl, () => { if (!suppressAutoSave.value) debouncedSaveAnalysis(); });

// Mirror anonymous trial state to sessionStorage (no-op for authenticated
// users — persistTrialSnapshot guards on that) so a reload can rehydrate it.
watch(
  [
    analysis,
    imageUrl,
    roomAnalysis,
    costAnalysis,
    () => store.state.studioStarterPack,
    () => store.state.evaluation,
    () => store.state.userIntent,
  ],
  () => { persistTrialSnapshot(); },
  { deep: true },
);

// Add new refs
const showUploadArea = ref(false);

// Add this ref near the top of the script section with other refs
const isUploading = ref(false);

// Add handler for upload success
const handleUploadSuccess = async (data) => {
  // Update store with new analysis data
  store.commit('setAnalysis', {
    analysis: data.analysis,
    imageUrl: data.imageUrl
  });
  
  // Reset lastAnalyzedImage for new upload
  store.commit('setLastAnalyzedImage', null);
  
  // Reset upload area visibility
  showUploadArea.value = false;
  
  isUploading.value = false;
  void preloadSupportingAnalyses({
    analysisData: data.analysis,
    targetImageUrl: data.imageUrl,
  })

  if (route.name === 'Analysis') {
    await router.replace({
      name: 'AnalysisHub',
      params: { id: store.state.analysis.analysisId || 'current' },
    })
  }
};

// Add this method in the script section
const handleUploadStateChange = (uploading) => {
  isUploading.value = uploading;
};

// Modify the handleBuyCredits function
const handleBuyCredits = () => {
  if (analysis.value && !hasPremiumAccess.value) {
    scrollToUnlockPanel()
    return
  }

  if (analysis.value && authStore.isAuthenticated) {
    showAnalysisPayPal.value = true
    return
  }

  const premiumSection = document.querySelector('.premium-features-card');
  if (premiumSection) {
    premiumSection.scrollIntoView({ behavior: 'smooth' });
  }
};

const trackProductEvent = (eventName, payload = {}) => {
  trackEvent(eventName, {
    analysis_id: analysisRouteId.value,
    auth_state: authStore.isAuthenticated ? 'authenticated' : 'anonymous',
    locale: locale.value,
    purpose: store.state.userIntent?.purpose || null,
    expectations_present: Boolean(store.state.userIntent?.expectations?.trim()),
    current_credit_balance: authStore.credits,
    ...payload,
  })
}

// The top-of-page product switcher was retired in favour of the unified
// side menu. The corresponding `product_switcher_clicked` telemetry event
// is now fired from inside the menu's router-link clicks (or, if we want
// to keep parity, can be re-added by wiring an `@navigate` emit on the
// side menu).

const routeNameForProductMode = (mode) => {
  if (mode === 'studio') return 'AnalysisStudio'
  if (mode === '360') return 'Analysis360'
  return 'AnalysisHub'
}

const canonicalizeCurrentAnalysisRoute = async (analysisId = store.state.analysis.analysisId) => {
  if (!analysisId || route.params.id !== 'current' || productMode.value === null) return
  await router.replace({
    name: routeNameForProductMode(productMode.value),
    params: { id: analysisId },
  })
}

const STUDIO_UNLOCK_EVENT_MAP = {
  branch: 'studio_branch_created',
  execute: 'studio_suggestion_executed',
  premium_preview: 'studio_premium_preview_clicked',
}

const STUDIO_UNLOCK_ACTION_MAP = {
  branch: 'studio_branch',
  execute: 'studio_step_image',
  premium_preview: 'studio_premium_preview',
}

const handleStudioUnlock = async (payload = {}) => {
  const kind = typeof payload === 'object' && payload?.kind ? payload.kind : 'execute'
  const action = STUDIO_UNLOCK_ACTION_MAP[kind] || 'studio_step_image'
  const intentEvent = STUDIO_UNLOCK_EVENT_MAP[kind]

  const eventProps = {
    suggestion_id: payload?.suggestionId || null,
    archetype: payload?.archetype || null,
  }

  if (intentEvent) {
    trackProductEvent(intentEvent, eventProps)
  }

  trackProductEvent('trial_paywall_hit', {
    action,
    ...eventProps,
  })

  // Capture the user's Studio intent so it survives signup + re-upload.
  // Without this, a trial user who clicks Execute, signs up, and uploads
  // their floor plan loses both the directive they were about to run
  // and the expectations they typed in the user-query modal.
  try {
    captureStudioHandoff({
      intent: {
        kind,
        directive: payload?.directive || null,
        directiveSource: payload?.directiveSource || null,
        archetype: payload?.archetype || null,
        suggestionId: payload?.suggestionId || null,
      },
      userIntent: store.state.userIntent || {},
      source: kind === 'premium_preview' ? 'premium_preview' : 'studio_paywall',
    })
  } catch (err) {
    // Best-effort. Don't fail the paywall flow on a localStorage hiccup.
    console.warn('captureStudioHandoff failed:', err?.message)
  }

  await handleUnlockCta()
}

const handleStudioSuggestionSeen = ({ suggestionId, archetype } = {}) => {
  trackProductEvent('studio_suggestion_seen', {
    suggestion_id: suggestionId || null,
    archetype: archetype || null,
  })
}

// Studio v2 — live tree state
const studioTree = ref({
  nodes: [],
  currentNodeId: null,
  pending: null,
  busy: false,
  stubMode: false,
  loadedFor: null,
})
const studioDriftDismissed = ref(false)

// ─ Derived state for the unified side menu ─────────────────────────────
// We render the Studio sub-tree + actions inside `AnalysisSideMenu` now
// instead of in StudioDashboard's internal aside, so the menu needs the
// same orderedNodes / capability flags the dashboard used to compute.
const orderedStudioNodes = computed(() => {
  const nodes = studioTree.value.nodes || []
  const list = [...nodes]
  list.sort((a, b) => {
    const ad = a.depth ?? 0
    const bd = b.depth ?? 0
    if (ad !== bd) return ad - bd
    return String(a.createdAt || '').localeCompare(String(b.createdAt || ''))
  })
  return list
})
const studioCurrentNode = computed(() => {
  const id = studioTree.value.currentNodeId
  if (!id) return null
  return orderedStudioNodes.value.find((n) => n.id === id) || null
})
const studioCanExportBranch = computed(() => {
  const current = studioCurrentNode.value
  return Boolean(current && current.parentNodeId !== null)
})
let activeStudioRequest = null
let activeStudioIdempotencyKey = null
// Set while a user-initiated cancel is being processed so the step's own
// catch block defers the "cancelled" toast to the cancel handler — which
// only promises a refund once the backend has actually acknowledged it.
let activeStudioCancelHandled = false

const studioDriftHintVisible = computed(() => {
  if (studioDriftDismissed.value) return false
  if (!studioTree.value.currentNodeId) return false
  const byId = new Map(studioTree.value.nodes.map((node) => [node.id, node]))
  let cursor = byId.get(studioTree.value.currentNodeId)
  const directives = []
  while (cursor && cursor.parentNodeId) {
    directives.push(cursor)
    cursor = byId.get(cursor.parentNodeId)
  }
  const geometricArchetypes = new Set(['merge_rooms', 'open_wall', 'add_room', 'relocate_entrance'])
  const geometricSteps = directives.filter((node) => geometricArchetypes.has(node.archetype)).length
  return directives.length >= 4 || geometricSteps >= 3
})

const refreshStudioTree = async (analysisId) => {
  if (!analysisId || analysisId === 'current' || !authStore.isAuthenticated) {
    studioTree.value = { nodes: [], currentNodeId: null, pending: null, busy: false, stubMode: false, loadedFor: null }
    return
  }
  try {
    const token = await getIdToken(authStore.user)
    const response = await axios.get(`/api/studio/tree/${analysisId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
    const data = response.data?.data || {}
    studioTree.value = {
      nodes: Array.isArray(data.nodes) ? data.nodes : [],
      currentNodeId: data.currentNodeId || null,
      pending: null,
      busy: false,
      stubMode: Boolean(data.stubMode),
      loadedFor: analysisId,
    }
  } catch (err) {
    if (err?.response?.status === 404) {
      // No analysis stored yet for this id → leave tree empty silently.
      studioTree.value = { nodes: [], currentNodeId: null, pending: null, busy: false, stubMode: false, loadedFor: analysisId }
      return
    }
    console.warn('Failed to refresh studio tree:', err?.message)
  }
}

const handleStudioGenerate = async (payload = {}) => {
  if (!hasPremiumAccess.value) {
    await handleStudioUnlock({ kind: payload?.suggestionId ? 'execute' : 'free_text', ...payload })
    return
  }
  const analysisId = store.state.analysis.analysisId
  if (!analysisId) {
    showNotification(t('analysis.studio.notReadyYet'), 'warning')
    return
  }
  // A new step always extends from the explicitly-passed parent or the
  // currently-selected node. To create a variant from an earlier point,
  // the user selects that node in the path view first, then generates.
  const parentNodeId = payload.parentNodeId || studioTree.value.currentNodeId

  const directive = String(payload.directive || '').trim()
  if (!directive) return

  studioTree.value = {
    ...studioTree.value,
    busy: true,
    pending: { directive, suggestionId: payload.suggestionId || null, archetype: payload.archetype || null },
  }
  trackProductEvent('studio_step_started', {
    directive_source: payload.directiveSource || 'free_text',
    archetype: payload.archetype || null,
    parent_node_id: parentNodeId,
  })

  try {
    const token = await getIdToken(authStore.user)
    // Idempotency: a stable client-supplied key lets the backend collapse a
    // retry/double-submit into the original step instead of charging again.
    const idempotencyKey = (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
    activeStudioIdempotencyKey = idempotencyKey
    activeStudioRequest = new AbortController()
    const response = await axios.post('/api/studio/step', {
      analysisId,
      parentNodeId,
      directive,
      directiveSource: payload.directiveSource || 'free_text',
      archetype: payload.archetype || null,
      widgetInputs: payload.widgetInputs || null,
      // Drives the language of the generated copy on the backend.
      locale: locale?.value || null,
      // Price lock: the cost we displayed for this step. If the catalog
      // changed since render, the backend rejects with PRICE_CHANGED instead
      // of silently charging a different amount.
      expectedCost: Number.isFinite(productHub.value?.studioStepCredits)
        ? productHub.value.studioStepCredits
        : null,
    }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Idempotency-Key': idempotencyKey,
      },
      // gpt-image-2 routinely takes 20-90 s and up to two retries. Cap the
      // client-side wait at 4 minutes to give the backend room to finish.
      timeout: 240000,
      signal: activeStudioRequest.signal,
    })

    const node = response.data?.data?.node
    if (node) {
      await authStore.fetchCredits()
      // Re-fetch the full tree so we pick up the root (materialised by the
      // backend on the first step) and any drift in sibling state. Falls
      // back to a local merge if the network refresh fails.
      try {
        await refreshStudioTree(analysisId)
        studioTree.value = {
          ...studioTree.value,
          currentNodeId: node.id,
          pending: null,
          busy: false,
          stubMode: Boolean(response.data?.data?.stubMode),
        }
      } catch (refreshErr) {
        studioTree.value = {
          ...studioTree.value,
          nodes: [...studioTree.value.nodes.filter((n) => n.id !== node.id), node],
          currentNodeId: node.id,
          pending: null,
          busy: false,
          stubMode: Boolean(response.data?.data?.stubMode),
        }
      }
      studioDriftDismissed.value = false
      // If this step replayed a captured handoff intent, mark the
      // conversion as complete and drop the localStorage payload so a
      // second visit doesn't re-seed the command bar.
      try {
        const handoff = readStudioHandoff({ peek: true })
        if (handoff?.intent?.directive
            && typeof directive === 'string'
            && handoff.intent.directive.trim() === directive.trim()) {
          trackProductEvent('studio_handoff_replay_succeeded', {
            directive_source: handoff.intent.directiveSource || null,
            replay_age_seconds: Math.round((Date.now() - handoff.capturedAt) / 1000),
          })
          clearStudioHandoff()
        }
      } catch (_) { /* best-effort */ }
      trackProductEvent('studio_step_completed', {
        directive_source: payload.directiveSource || 'free_text',
        archetype: node.archetype,
        node_id: node.id,
        latency_ms: node.providerMeta?.latency_ms || null,
        cost: node.creditCost || 0,
        replayed: Boolean(response.data?.data?.replayed),
      })
    } else {
      throw new Error('Empty step response')
    }
  } catch (err) {
    studioTree.value = { ...studioTree.value, busy: false, pending: null }
    const code = err?.response?.data?.error || err?.message || 'unknown'
    const status = err?.response?.status
    const retryAfter = err?.response?.data?.retryAfterSeconds || Number(err?.response?.headers?.['retry-after']) || null
    trackProductEvent('studio_step_failed', {
      directive_source: payload.directiveSource || 'free_text',
      reason: code,
      status,
    })
    if (axios.isCancel?.(err) || err?.code === 'ERR_CANCELED' || code === 'STEP_CANCELLED') {
      // When the user cancelled, the cancel handler owns the messaging —
      // it knows whether the backend actually acknowledged the refund. Only
      // fall back to the optimistic message for a cancel we didn't initiate
      // (e.g. a server-side STEP_CANCELLED with no local handler in flight).
      if (!activeStudioCancelHandled) {
        showNotification(t('analysis.studio.cancelled'), 'info')
      }
      return
    }
    if (status === 402) {
      showNotification(t('analysis.studio.errors.insufficientCredits'), 'error')
      handleBuyCredits()
      return
    }
    if (status === 429 || /RATE_LIMITED/.test(code)) {
      showNotification(t('analysis.studio.rateLimited', { seconds: retryAfter || 60 }), 'warning')
      return
    }
    if (code === 'CONTENT_FLAGGED') {
      showNotification(t('analysis.studio.contentFlagged'), 'warning')
      return
    }
    if (code === 'PRICE_CHANGED') {
      // The price moved between render and submit; nothing was charged.
      // Force-refresh the catalog (bypassing the still-valid cache that
      // produced the stale price) so the CTA/cards show the new cost, then
      // tell the user the new price. The backend echoes the actual cost.
      await refreshPricingRules({ force: true })
      const actual = err?.response?.data?.actualCost
      const fallbackPrice = Number.isFinite(productHub.value?.studioStepCredits)
        ? productHub.value.studioStepCredits
        : ''
      showNotification(
        t('analysis.studio.priceChanged', { price: Number.isFinite(actual) ? actual : fallbackPrice }),
        'warning',
      )
      return
    }
    console.error('Studio step failed:', err)
    showNotification(t('analysis.studio.errors.generationFailed'), 'error')
  } finally {
    activeStudioRequest = null
    activeStudioIdempotencyKey = null
  }
}

const handleStudioCancelGeneration = async () => {
  if (!activeStudioIdempotencyKey || !studioTree.value.busy) return
  const analysisId = store.state.analysis.analysisId
  const idempotencyKey = activeStudioIdempotencyKey
  trackProductEvent('studio_step_cancelled', {
    directive_source: studioTree.value.pending?.directiveSource || null,
    latency_ms: null,
  })
  // The step's own catch block will fire as soon as we abort the request;
  // tell it to stay quiet so we can post the single authoritative message
  // once we know whether the backend accepted the cancellation.
  activeStudioCancelHandled = true

  // Tell the backend to mark the in-flight slot as cancelled — it will
  // refund the credit + delete the file when the provider call returns.
  // `cancelConfirmed` stays false unless the backend reports `ok:true`; a
  // network failure or a "committed" response means the step may already
  // have been charged, so we must NOT claim a refund.
  let cancelConfirmed = false
  try {
    const token = await getIdToken(authStore.user)
    const { data } = await axios.post('/api/studio/cancel', {
      analysisId,
      idempotencyKey,
    }, { headers: { Authorization: `Bearer ${token}`, 'Idempotency-Key': idempotencyKey } })
    cancelConfirmed = data?.data?.ok === true && data?.data?.status === 'cancelled'
  } catch (err) {
    console.warn('Studio cancel signal failed (continuing with local abort):', err?.message)
  }

  // Abort the local axios request so the UI can recover immediately without
  // waiting for the provider round-trip to complete.
  if (activeStudioRequest) {
    try { activeStudioRequest.abort() } catch (_) { /* ignore */ }
  }
  studioTree.value = { ...studioTree.value, busy: false, pending: null }

  // Re-sync credits and the tree so the UI reflects what actually happened
  // server-side rather than our optimistic local abort.
  await authStore.fetchCredits()
  try { await refreshStudioTree(analysisId) } catch (_) { /* best-effort */ }

  if (cancelConfirmed) {
    showNotification(t('analysis.studio.cancelled'), 'info')
  } else {
    // The cancel was not acknowledged (already committed, or the signal
    // failed). Be honest: the step may have completed and charged.
    showNotification(t('analysis.studio.cancelUnconfirmed'), 'warning')
  }
  activeStudioCancelHandled = false
}

const handleStudioSelectNode = async ({ nodeId } = {}) => {
  if (!nodeId) return
  const analysisId = store.state.analysis.analysisId
  if (!analysisId) return
  const previous = studioTree.value.currentNodeId
  studioTree.value = { ...studioTree.value, currentNodeId: nodeId }
  trackProductEvent('studio_node_selected', { node_id: nodeId, was_current: previous === nodeId })
  try {
    const token = await getIdToken(authStore.user)
    await axios.post('/api/studio/select-node', { analysisId, nodeId }, {
      headers: { Authorization: `Bearer ${token}` },
    })
  } catch (err) {
    console.warn('Failed to persist node selection:', err?.message)
  }
}

const studioExportBusy = ref(false)

const handleStudioExportBranch = async () => {
  if (studioExportBusy.value) return
  if (!hasPremiumAccess.value) {
    await handleStudioUnlock({ kind: 'execute', archetype: 'export_pdf' })
    return
  }
  const analysisId = store.state.analysis.analysisId
  if (!analysisId || !studioTree.value.currentNodeId) return
  studioExportBusy.value = true
  const branchLength = (() => {
    const byId = new Map(studioTree.value.nodes.map((n) => [n.id, n]))
    let cursor = byId.get(studioTree.value.currentNodeId)
    let count = 0
    while (cursor) {
      count += 1
      cursor = cursor.parentNodeId ? byId.get(cursor.parentNodeId) : null
    }
    return count
  })()
  trackProductEvent('studio_export_started', { branch_length: branchLength })
  const startedAt = Date.now()
  let chargeEventId = null
  try {
    const token = await getIdToken(authStore.user)
    const chargeResponse = await axios.post('/api/studio/export-charge', { analysisId, branchLength }, {
      headers: { Authorization: `Bearer ${token}` },
    })
    chargeEventId = chargeResponse.data?.data?.chargeEventId || null
    await authStore.fetchCredits()
    await exportStudioBranchPdf({
      nodes: studioTree.value.nodes,
      currentNodeId: studioTree.value.currentNodeId,
      userQuestion: store.state.userIntent?.expectations || '',
      evaluation: store.state.evaluation || '',
      suggestions: analysis.value?.suggestions || [],
      locale: locale.value,
    })
    trackProductEvent('studio_export_succeeded', {
      branch_length: branchLength,
      latency_ms: Date.now() - startedAt,
    })
  } catch (err) {
    const code = err?.response?.data?.error || err?.message || 'unknown'
    if (!err?.response && analysisId && chargeEventId) {
      try {
        const token = await getIdToken(authStore.user)
        await axios.post('/api/studio/export-refund', {
          analysisId,
          chargeEventId,
          reason: code,
        }, { headers: { Authorization: `Bearer ${token}` } })
        await authStore.fetchCredits()
      } catch (refundErr) {
        console.error('Studio export refund failed:', refundErr)
      }
    }
    if (err?.response?.status === 402) {
      showNotification(t('analysis.studio.errors.insufficientCredits'), 'error')
      handleBuyCredits()
    } else {
      console.error('Studio export failed:', err)
      showNotification(t('analysis.studio.errors.exportFailed'), 'error')
    }
    trackProductEvent('studio_export_failed', { reason: code })
  } finally {
    studioExportBusy.value = false
  }
}

const handleStudioDismissDrift = () => {
  studioDriftDismissed.value = true
  trackProductEvent('studio_drift_hint_dismissed', {
    drift_score: 0,
  })
}

// Studio command-bar prefill priority:
//   1. ?suggestion=<id> in the URL → full directive read back from the
//      sessionStorage stash the hub wrote (keeps the URL short without
//      truncating the image-gen prompt).
//   2. ?prefill=… in the URL (legacy / externally-shared 360 deep-links)
//   3. studio_handoff localStorage payload (from a pre-signup paywall hit
//      that the user just resolved by signing up + paying for the upload)
// Once a handoff-sourced prefill is read, we leave the entry in
// localStorage until the user actually submits a step, at which point
// the success path clears it.
const studioPrefillText = computed(() => {
  const suggestionId = route.query?.suggestion
  if (typeof suggestionId === 'string' && suggestionId) {
    const stashed = readStudioPrefill(suggestionId)
    if (stashed) return stashed
  }
  const prefill = route.query?.prefill
  if (typeof prefill === 'string' && prefill.trim()) return prefill
  if (productMode.value === 'studio' && hasPremiumAccess.value && hasStudioHandoff()) {
    const handoff = readStudioHandoff({ peek: true })
    if (handoff?.intent?.directive) return handoff.intent.directive
  }
  return ''
})

watch(
  () => [route.query?.prefill, route.query?.suggestion, route.query?.source, route.query?.score, productMode.value],
  ([prefill, suggestion, source, score, mode]) => {
    if (mode !== 'studio' || (!prefill && !suggestion)) return
    const numericScore = Number(score)
    trackProductEvent('360_to_studio_deeplink_clicked', {
      source_section: source || 'unknown',
      prefill_text: String(prefill || studioPrefillText.value || '').slice(0, 120),
      score: Number.isFinite(numericScore) ? numericScore : null,
    })
  },
  { immediate: true },
)

const openAnalysisPurchase = async () => {
  await nextTick()
  showAnalysisPayPal.value = true
}

const handleUnlockCta = async () => {
  const unlockPayload = buildUnlockCtaPayload({
    source: 'analysis_panel',
    userIntent: store.state.userIntent,
    extra: {
      isAuthenticated: authStore.isAuthenticated,
    },
  })

  trackUnlockCtaClicked(unlockPayload)

  if (!authStore.isAuthenticated) {
    trackAuthGateOpened(unlockPayload)

    await router.push({
      path: '/sign-up',
      query: buildAnalysisUnlockQuery({ source: 'analysis_panel' }),
    })
    return
  }

  if (hasCreditsForNewPremiumAnalysis.value) {
    showNotification(t('analysis.payment.ready'), 'success')
    await openUploadAreaForPremiumRerun()
    return
  }

  await openAnalysisPurchase()
}

const resumeAnalysisUnlockFromQuery = async () => {
  const unlockIntent = readAnalysisUnlockIntent(route.query)

  if (!unlockIntent.shouldResumeUnlock) {
    return
  }

  await nextTick()
  scrollToUnlockPanel()

  if (unlockIntent.shouldOpenPurchase && authStore.isAuthenticated) {
    if (hasCreditsForNewPremiumAnalysis.value) {
      await openUploadAreaForPremiumRerun()
    } else {
      await openAnalysisPurchase()
    }
  }

  await router.replace({
    path: '/analysis',
    query: clearAnalysisUnlockQuery(route.query),
  })
}

const handleAnalysisPaymentSuccess = async () => {
  await authStore.fetchCredits()
  trackProductEvent('trial_to_paid_conversion', {
    source: 'analysis_panel',
  })
  showNotification(t('analysis.payment.success'), 'success')
  await openUploadAreaForPremiumRerun()
}

const handleAnalysisPaymentError = (error) => {
  console.error('Analysis payment failed:', error)
  showNotification(t('pricing.payment.error'), 'error')
}

// Add to script setup section:
const activeTab = ref(0)
// Track which report we've already chosen a landing tab for, so the default
// re-applies when a user uploads another floor plan or loads a saved analysis
// without navigating away from /analysis. Keyed on imageUrl because the
// analysis object can be reassigned during a single report (e.g. after the
// barrier-free merge), but imageUrl remains stable across that.
let lastLandingTabReportKey = null

// Stable, locale-independent keys mirroring the `tabs` computed below.
const TAB_KEYS = ['analysis', 'rooms', 'costs', 'improvements']

// Add a watch effect for tab changes
watch(activeTab, async (newTab) => {
  await nextTick()

  // On phones, keep the compact tab bar in view when switching sections.
  if (display.smAndDown.value) {
    document.querySelector('.analysis-section-mobile-tabs')?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  } else {
    scheduleReportShellAlignment()
  }

  // When the Improvements tab opens, attach the IntersectionObserver to the
  // teaser section so a premium user without a cached teaser triggers generation.
  if (newTab === 3) {
    void observeImprovementsTeaserSection();
  }
  const tabKey = TAB_KEYS[newTab] || String(newTab)
  trackAnalysisTabView({
    tab_key: tabKey,
    tab_index: newTab,
    is_initial: false,
  });
  if (productMode.value === '360') {
    trackProductEvent('360_section_viewed', {
      section: tabKey,
      tab_index: newTab,
    })
  }
});

// Add these computed properties:
const tabs = computed(() => [
  { title: t('analysis.tabs.analysis'), icon: 'mdi-clipboard-text' },
  { title: t('analysis.tabs.rooms'), icon: 'mdi-door' },
  { title: t('analysis.tabs.costs'), icon: 'mdi-currency-usd' },
  { title: t('analysis.tabs.improvements'), icon: 'mdi-lightbulb' }
])

// Modify the existing watch effect
watch(
  () => analysis.value,
  (newAnalysis) => {
    if (newAnalysis) {
      const reportKey = imageUrl.value || newAnalysis
      if (reportKey !== lastLandingTabReportKey) {
        activeTab.value = 0
        lastLandingTabReportKey = reportKey
      }
      maybeShowTrialPreviewBanner()
      trackAnalysisLoaded({
        hasPremiumAccess: hasPremiumAccess.value,
        hasUserIntent: hasUserExpectations.value,
        locale: window.document?.documentElement?.lang || undefined,
      })
      trackAnalysisTabView({
        tab_key: TAB_KEYS[activeTab.value] || String(activeTab.value),
        tab_index: activeTab.value,
        is_initial: true,
      })
    }
  },
  { immediate: true }
)

watch(hasPremiumAccess, () => {
  maybeShowTrialPreviewBanner()
})

watch(
  () => productMode.value,
  async (mode, previousMode) => {
    if (!analysis.value) return
    if (previousMode && mode && mode !== previousMode) {
      window.scrollTo({ top: 0, behavior: 'auto' })
    }
    if (mode === 'hub') trackProductEvent('hub_loaded')
    if (mode === 'studio') trackProductEvent('studio_opened')
    if (mode === '360') trackProductEvent('360_opened')
    await canonicalizeCurrentAnalysisRoute(store.state.analysis.analysisId)
    if (mode === 'studio' && store.state.analysis.analysisId) {
      void refreshStudioTree(store.state.analysis.analysisId)
    }
  },
  { immediate: true },
)

watch(
  () => store.state.analysis.analysisId,
  async (newId) => {
    if (!newId) return
    await canonicalizeCurrentAnalysisRoute(newId)
    if (studioTree.value.loadedFor !== newId) {
      void refreshStudioTree(newId)
    }
  },
)

watch(
  () => authStore.isAuthenticated,
  () => {
    void refreshPricingRules()
  },
)

watch(
  () => [analysis.value, imageUrl.value, store.state.userIntent],
  ([newAnalysis, newImageUrl]) => {
    if (newAnalysis && newImageUrl && !suppressAutoSave.value) {
      void preloadSupportingAnalyses({
        analysisData: newAnalysis,
        targetImageUrl: newImageUrl,
      })
    }
  },
  { deep: true }
)

watch(
  () => [analysis.value, imageUrl.value, store.state.userIntent?.expectations, hasPremiumAccess.value],
  () => {
    resetPersonalizedTeaserState()

    if (hasPremiumAccess.value) {
      void observeImprovementsTeaserSection()
    }
  },
  { deep: true }
)

watch(
  () => store.state.analysis,
  (current) => {
    const cached = current?.personalizedTeaserImageUrl
    if (cached && hasPremiumAccess.value) {
      teaserImageSrc.value = cached
      teaserStatus.value = 'ready'
      teaserRequested.value = true
    }
  },
  { immediate: true },
)
</script>

<style scoped>
.analysis {
  min-height: 100vh;
  background-color: var(--background);
}

.analysis-header {
  align-items: flex-start;
}

.analysis-product-switcher {
  justify-self: end;
}

.analysis-title-block {
  max-width: 520px;
}

.analysis-subtitle {
  color: var(--text-secondary);
}

/* Typography overrides */
h1 {
  color: var(--text-primary);
}

p {
  color: var(--text-secondary);
}

:deep(.v-card) {
  border-radius: 16px;
  transition: all 0.3s ease;
}

:deep(.v-card:hover) {
  box-shadow: var(--shadow-md);
}

.analysis-select {
  min-width: 300px;
  max-width: 400px;
  
  :deep(.v-field) {
    padding: 0 12px;
  }
  
  @media (max-width: 660px) {
    min-width: 100%;
  }
}

/* Add these new styles */
.d-flex.gap-2 {
  align-items: center;
}

.d-flex.gap-2 > * {
  flex-shrink: 0;
}

@media (max-width: 660px) {
  .d-flex.gap-2 {
    width: 100%;
  }
  
  .d-flex.gap-2 > * {
    flex: 1;
  }
  
  .d-flex.gap-2 .v-btn {
    width: 100%;
  }
}

.controls-wrapper {
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
}

.control-item {
  min-height: 44px;
  border-radius: 14px;
  text-transform: none;
  padding-inline: 14px;
}

.control-label {
  white-space: nowrap;
}

.control-icon {
  margin-right: 8px;
}

@media (max-width: 660px) {
  .analysis-title-block {
    width: 100%;
  }

  .analysis-product-switcher {
    width: 100%;
    justify-self: stretch;
  }

  .controls-wrapper {
    width: 100%;
    gap: 8px;
    justify-content: stretch;
  }
  
  .control-item {
    flex: 1 1 calc(50% - 4px);
    min-width: 0 !important;
    min-height: 58px !important;
    padding: 10px 12px !important;
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 6px;
  }
  
  .control-item.v-btn {
    margin: 0;
  }

  .control-item .v-icon {
    font-size: 20px;
    margin: 0 !important;
  }

  .control-label {
    white-space: normal;
    text-align: center;
    line-height: 1.15;
    font-size: calc(var(--font-size-body-2) - 0.0625rem);
  }

}

.gap-4 {
  gap: 1rem;
}

/* Add these new styles */
:deep(.v-slide-y-transition-enter-active),
:deep(.v-slide-y-transition-leave-active) {
  transition-duration: 0.3s !important;
}

/* Add these new styles */
.preview-row {
  min-height: 300px;
}

.preview-row .v-col {
  height: 100%;
}

.analysis-container {
  max-width: none;
  padding-inline: 24px;
}

/* Side menu + main content shell. The `:has()` selector keeps the
 * 2-column grid only when an actual rail is rendered (either the
 * full AnalysisSideMenu or the upload-state AppAccountRail
 * fallback) — for routes that intentionally render no rail the main
 * content occupies the full row instead of being squeezed into an
 * empty sidebar slot. */
.analysis-container--with-side-menu .analysis-shell:has(.analysis-shell-side) {
  display: grid;
  grid-template-columns: minmax(220px, 260px) minmax(0, 1fr);
  gap: 18px;
  align-items: start;
}
.analysis-shell-main {
  min-width: 0;
}

.analysis-upload-intro {
  max-width: 760px;
  margin: 0 auto 18px;
  text-align: center;
}

.analysis-upload-intro h1 {
  margin: 0 0 8px;
  color: var(--text-primary);
  font-size: clamp(1.7rem, 3vw, 2.45rem);
  line-height: 1.12;
  letter-spacing: 0;
}

.analysis-upload-intro p {
  margin: 0;
}

.analysis-upload-eyebrow {
  margin-bottom: 8px !important;
  color: rgb(var(--v-theme-primary)) !important;
  font-size: var(--font-size-caption);
  font-weight: var(--font-weight-semibold);
  letter-spacing: 0;
  text-transform: uppercase;
}

@media (max-width: 960px) {
  .analysis-container--with-side-menu .analysis-shell:has(.analysis-shell-side) {
    display: block;
  }
  .analysis-shell-side {
    margin-bottom: 12px;
  }
}

.analysis-report-shell {
  /* The 360 sub-nav now lives in the unified AnalysisSideMenu on desktop
   * and only renders inline on mobile (via $vuetify.display.smAndDown).
   * The old grid reserved a fixed left column for the inline nav; with
   * the nav gone on desktop the content needs the full width. */
  display: block;
  margin-top: 16px;
}
@media (max-width: 959px) {
  .analysis-report-shell {
    display: grid;
    grid-template-columns: minmax(192px, 224px) minmax(0, 1fr);
    gap: 12px;
    align-items: start;
  }
}

.analysis-report-content {
  min-width: 0;
}

.trial-preview-region {
  padding-top: clamp(8px, 1vw, 16px);
}

@media (max-width: 959px) {
  .analysis-report-shell {
    display: block;
    margin-top: 12px;
  }

  .analysis-report-content {
    margin-top: 16px;
  }
}

:deep(.v-window) {
  height: auto !important;
}

:deep(.v-window__container) {
  height: auto !important;
}

:deep(.v-window-item) {
  height: auto !important;
}

/* Add some padding to the bottom of each tab content */
:deep(.v-window-item) > * {
  padding-bottom: 24px;
}

/* Add some transition effects */
:deep(.v-window-item) {
  transition: opacity 0.3s ease;
}

:deep(.v-window-item--active) {
  opacity: 1;
}

</style>
